import type { ISourceOptions } from '@tsparticles/engine';
import type { IntegrationParticleGeneratorState } from '@/src/components/integrations/particle-generator/Model';

const getBaseConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  const baseConfig: ISourceOptions = {
    autoPlay: true,
    fpsLimit: 120,
    fullScreen: {
      enable: true,
      zIndex: -1
    }
  };

  if ((state.flowpage.enabled || state.section.enabled) && baseConfig.fullScreen) {
    baseConfig.fullScreen = {
      enable: false,
      zIndex: -1
    };
  }

  return baseConfig;
};

export const getSnowConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  return {
    ...getBaseConfig(state),
    particles: {
      move: {
        direction: 'bottom',
        enable: true,
        outModes: 'out',
        speed: 3
      },
      number: {
        density: {
          enable: true
        },
        value: 40
      },
      opacity: {
        value: 0.7
      },
      rotate: {
        random: {
          enable: true,
          minimumValue: 0
        },
        value: 20,
        animation: {
          enable: true,
          speed: 5,
          decay: 0,
          sync: false
        }
      },
      shape: {
        type: 'images',
        options: {
          images:
            state && state.particle?.images
              ? state.particle.images.map((flagImage) => ({
                  src: flagImage
                }))
              : []
        }
      },

      size: {
        value: { min: 2, max: 5 } // Adjust for desired snowflake size
      },
      wobble: {
        enable: true,
        distance: 5,
        speed: 10
      },
      zIndex: {
        value: { min: 0, max: 50 }
      }
    }
  };
};

export const getConfettiConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  return {
    ...getBaseConfig(state),
    particles: {
      color: {
        value: ['#1E00FF', '#FF0061', '#E1FF00', '#00FF9E']
      },
      move: {
        direction: 'bottom',
        enable: true,
        outModes: {
          default: 'out'
        },
        size: true,
        speed: {
          min: 1,
          max: 3
        }
      },
      number: {
        value: 500,
        density: {
          enable: true,
          area: 800
        }
      },
      opacity: {
        value: 1,
        animation: {
          enable: false,
          startValue: 'max',
          destroy: 'min',
          speed: 0.3,
          sync: true
        }
      },
      rotate: {
        value: {
          min: 0,
          max: 360
        },
        direction: 'random',
        move: true,
        animation: {
          enable: true,
          speed: 60
        }
      },
      tilt: {
        direction: 'random',
        enable: true,
        move: true,
        value: {
          min: 0,
          max: 360
        },
        animation: {
          enable: true,
          speed: 60
        }
      },
      shape: {
        type: ['circle', 'square'],
        options: {}
      },
      size: {
        value: {
          min: 2,
          max: 4
        }
      },
      roll: {
        darken: {
          enable: true,
          value: 30
        },
        enlighten: {
          enable: true,
          value: 30
        },
        enable: true,
        speed: {
          min: 15,
          max: 25
        }
      },
      wobble: {
        distance: 30,
        enable: true,
        move: true,
        speed: {
          min: -15,
          max: 15
        }
      }
    }
  } as ISourceOptions;
};

export const getFlagsConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  return {
    ...getBaseConfig(state),
    particles: {
      tilt: {
        random: {
          enable: true,
          minimumValue: 0
        },
        value: {
          min: 0,
          max: 5
        },
        animation: {
          enable: true,
          speed: 5,
          decay: 0,
          sync: false
        },
        direction: 'random',
        enable: true
      },
      move: {
        direction: 'bottom',
        enable: true,
        outModes: 'out',
        speed: 4
      },
      number: {
        density: {
          enable: true
        },
        value: 70
      },
      rotate: {
        random: {
          enable: true,
          minimumValue: 20
        },
        value: 0,
        animation: {
          enable: true,
          speed: 12,
          decay: 0,
          sync: false
        }
      },
      shape: {
        type: 'images',
        options: {
          images:
            state && state.particle?.images
              ? state.particle.images.map((flagImage) => ({
                  src: flagImage
                }))
              : []
        }
      },
      size: {
        value: 12
      },
      wobble: {
        enable: true,
        distance: 5,
        speed: 10
      },
      zIndex: {
        value: { min: 0, max: 100 }
      }
    }
  };
};

export const getCustomConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  return {
    ...getBaseConfig(state),
    particles: {
      color: { value: '#fff' },
      move: {
        direction: 'bottom',
        enable: true,
        outModes: 'out',
        speed: 2
      },
      number: {
        density: {
          enable: true
        },
        value: 50
      },
      rotate: {
        random: {
          enable: true,
          minimumValue: 0
        },
        value: 0,
        animation: {
          enable: true,
          speed: 5,
          decay: 0,
          sync: false
        }
      },
      shape: {
        type: 'images',
        options: {
          images:
            state && state.particle?.images
              ? state.particle.images.map((flagImage) => ({
                  src: flagImage
                }))
              : []
        }
      },
      size: {
        value: 30
      },
      wobble: {
        enable: true,
        distance: 10,
        speed: 10
      },
      zIndex: {
        value: { min: 0, max: 100 }
      }
    }
  };
};
